import React from "react";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { ButtonGroup } from "@caelor/ui";
import { ICON_PROP, LAYOUT_PROPS, TITLETEXT_PROPS } from "./defaults";
import { useTitleTextStore } from "../../../../../../stores/use-titletext-store";
import { useTranslation } from "react-i18next";
import { ALIGNMENT_OPTIONS, BOOLEAN_OPTIONS } from "./options";
import {
  IconSizeOptions,
  LineWeightOptions,
  LineWidthOptions,
  OverlineSizeOptions,
  TextSizeOptions,
  TitleSizeOptions,
} from "./sizing-options";
import IconPicker from "../../../../../../components/ui/icon-picker/IconPicker";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";

const TextAndTitleSettings = () => {
  const { t } = useTranslation();
  const { data, setData } = useTitleTextStore((state) => ({
    data: state.data,
    setData: state.setData,
  }));

  const handleLayoutChange = (key, value) => {
    const newLayout = { ...data[TITLETEXT_PROPS.LAYOUT], [key]: value };
    setData({ [TITLETEXT_PROPS.LAYOUT]: newLayout });
  };

  const getColor = (key) => {
    return {
      accent: {
        light: data.colorsByColorScheme.light[key],
        dark: data.colorsByColorScheme.dark[key],
      },
      text: {
        light: data.colorsByColorScheme.light[key],
        dark: data.colorsByColorScheme.dark[key],
      },
    };
  };

  const handleColorChange = (key, { light, dark }) => {
    const colors = {
      ...data.colorsByColorScheme,
      light: {
        ...data.colorsByColorScheme.light,
        [key]: light,
      },
      dark: {
        ...data.colorsByColorScheme.dark,
        [key]: dark,
      },
    };

    setData({ [TITLETEXT_PROPS.COLORS_BY_COLOR_SCHEME]: colors });
  };

  return (
    <>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("alignment")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("align")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={ALIGNMENT_OPTIONS}
              value={data[TITLETEXT_PROPS.ALIGNMENT]}
              onClick={(value) =>
                setData({ [TITLETEXT_PROPS.ALIGNMENT]: value })
              }
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("flexible-display-items")}
        </SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("icon")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.ICON]}
              onClick={(isOn) => handleLayoutChange(LAYOUT_PROPS.ICON, isOn)}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("title")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.TITLE]}
              onClick={(isOn) => handleLayoutChange(LAYOUT_PROPS.TITLE, isOn)}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("text")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.TEXT]}
              onClick={(isOn) => handleLayoutChange(LAYOUT_PROPS.TEXT, isOn)}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("overline")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.OVERLINE]}
              onClick={(isOn) =>
                handleLayoutChange(LAYOUT_PROPS.OVERLINE, isOn)
              }
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("line")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.LINE]}
              onClick={(isOn) => handleLayoutChange(LAYOUT_PROPS.LINE, isOn)}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>

      {data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.ICON] && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("content")}</SettingsOptionHeading>
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <IconPicker
                defaultTab={
                  data[TITLETEXT_PROPS.EMOJI_ENABLED] ? "Emojis" : "Icons"
                }
                tabs={["Icons", "Emojis", "Logos"]}
                placeholder={t("add")}
                icon={
                  data[TITLETEXT_PROPS.EMOJI_ENABLED]
                    ? data[TITLETEXT_PROPS.EMOJI]
                    : data[TITLETEXT_PROPS.ICON]
                }
                onChange={({ value, type }) => {
                  setData({
                    [ICON_PROP[type.replace("app", "icon")]]: value,
                    [TITLETEXT_PROPS.EMOJI_ENABLED]: type === "emoji",
                  });
                }}
              />
            </OptionContent>
          </OptionContainer>
        </SettingsOptionContainer>
      )}

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>
        {data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.ICON] && (
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={IconSizeOptions}
                value={data[TITLETEXT_PROPS.ICON_SIZE]}
                options={{ input: true }}
                onClick={(value) =>
                  setData({ [TITLETEXT_PROPS.ICON_SIZE]: value })
                }
                onChange={({ target: { value } }) =>
                  setData({ [TITLETEXT_PROPS.ICON_SIZE]: value })
                }
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
        {data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.OVERLINE] && (
          <OptionContainer>
            <OptionLabel>{t("overline")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={OverlineSizeOptions}
                value={data[TITLETEXT_PROPS.OVERLINE_SIZE]}
                options={{ input: true }}
                onClick={(value) =>
                  setData({ [TITLETEXT_PROPS.OVERLINE_SIZE]: value })
                }
                onChange={({ target: { value } }) =>
                  setData({ [TITLETEXT_PROPS.OVERLINE_SIZE]: value })
                }
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
        {data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.TITLE] && (
          <OptionContainer>
            <OptionLabel>{t("title")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={TitleSizeOptions}
                value={data[TITLETEXT_PROPS.TITLE_SIZE]}
                options={{ input: true }}
                onClick={(value) =>
                  setData({ [TITLETEXT_PROPS.TITLE_SIZE]: value })
                }
                onChange={({ target: { value } }) =>
                  setData({ [TITLETEXT_PROPS.TITLE_SIZE]: value })
                }
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
        {data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.TEXT] && (
          <OptionContainer>
            <OptionLabel>{t("text")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={TextSizeOptions}
                value={data[TITLETEXT_PROPS.TEXT_SIZE]}
                options={{ input: true }}
                onClick={(value) =>
                  setData({ [TITLETEXT_PROPS.TEXT_SIZE]: value })
                }
                onChange={({ target: { value } }) =>
                  setData({ [TITLETEXT_PROPS.TEXT_SIZE]: value })
                }
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
        {data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.LINE] && (
          <OptionContainer>
            <OptionLabel>{t("line-width")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={LineWidthOptions}
                value={data[TITLETEXT_PROPS.LINE_WIDTH]}
                options={{ input: true }}
                onClick={(value) =>
                  setData({ [TITLETEXT_PROPS.LINE_WIDTH]: value })
                }
                onChange={({ target: { value } }) =>
                  setData({ [TITLETEXT_PROPS.LINE_WIDTH]: value })
                }
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
        {data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.LINE] && (
          <OptionContainer>
            <OptionLabel>{t("line-weight")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={LineWeightOptions}
                value={data[TITLETEXT_PROPS.LINE_WEIGHT]}
                options={{ input: true }}
                onClick={(value) =>
                  setData({ [TITLETEXT_PROPS.LINE_WEIGHT]: value })
                }
                onChange={({ target: { value } }) =>
                  setData({ [TITLETEXT_PROPS.LINE_WEIGHT]: value })
                }
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        {(data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.ICON] ||
          data[TITLETEXT_PROPS.LAYOUT][LAYOUT_PROPS.OVERLINE]) && (
          <OptionContainer>
            <OptionLabel alignToTop>{t("colors")}</OptionLabel>
            <OptionContent>
              <ColorPicker
                hideColorTiles
                value={getColor(TITLETEXT_PROPS.ICON_COLOR)}
                onChange={({ accent }) => {
                  handleColorChange(TITLETEXT_PROPS.ICON_COLOR, accent);
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>
    </>
  );
};

export default TextAndTitleSettings;
